import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Helmet } from 'react-helmet';

import config from '../../../../config.js';
import { getFile } from '../../../../util/file.js';
import { stripTags } from '../../../../util/text.js';

import { getContentBlockItemsByName } from '../../../Cms/CmsActions.js';
import { getDefaultLanguage, getLanguagePath, getTranslation } from '../../../Intl/IntlHelper.js';
import { getProductAttributeList, getProductPrice, getProductSlug, memoizedGetProducts } from '../../ProductActions.js';
import { getProductOfferSchema } from '../../ProductSchema.js';

// Returns the active products belonging to the category page (same matching logic as ProductsListWrapper).
export function getCategoryProducts(products, page, locale) {
	const categories = getContentBlockItemsByName((page || {}).blocks, 'category').map(cat => cat.text).filter(cat => cat).map(cat => `${cat}`.toLowerCase());
	if(!categories.length) {
		return [];
	}
	return products.filter(product => [
		...getProductAttributeList(product, 'categories', locale),
		...getProductAttributeList(product, 'categories', getDefaultLanguage()),
	].some(productCategory => categories.includes(`${productCategory}`.toLowerCase())));
}

function CategorySeo({ intl, page, title = '', description = '', cover = '', products = [] }) {
	const { locale } = intl;
	const categoryProducts = getCategoryProducts(products, page, locale);

	const firstProductImage = categoryProducts.length && (categoryProducts[0].pictures || [])[0]
		? getFile(categoryProducts[0].pictures[0])
		: '';

	// Cover if set, otherwise fall back to the first product image.
	const snippetImage = cover ? getFile(cover) : firstProductImage;

	const pageUrl = `${config.url}${getLanguagePath(`/${((page || {}).slug || '').replace('*', '')}`, locale).substring(1)}`;

	const itemList = categoryProducts.length > 0 && {
		'@type': 'ItemList',
		name: title,
		numberOfItems: categoryProducts.length,
		itemListElement: categoryProducts.map((product, index) => {
			const url = `${config.url}${locale}/${getProductSlug(product, locale)}`;
			const price = getProductPrice(product, null, null, true, null);
			return {
				'@type': 'ListItem',
				position: index + 1,
				item: {
					'@type': 'Product',
					name: getTranslation(product, 'name', locale),
					description: getTranslation(product, 'name', locale), // no description in product data (but in cms)
					url,
					image: (product.pictures || [])[0] ? getFile(product.pictures[0]) : undefined,
					gtin: product.gtin,
					...(price > 0 ? {
						offers: getProductOfferSchema(product, { locale, price: Number(price).toFixed(2), url }),
					} : {}),
				},
			};
		}),
	};

	// A category is a CollectionPage, not a Product: it carries no offer, no SKU and no
	// rating of its own. The products it lists are exposed through mainEntity / ItemList,
	// where each item keeps its own Offer.
	const collectionPageLd = JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'CollectionPage',
		name: title,
		...(description ? { description: stripTags(description) } : {}),
		url: pageUrl,
		...(snippetImage ? { image: snippetImage } : {}),
		isPartOf: {
			'@type': 'WebSite',
			name: config.application.name,
			url: `${config.url}${locale}/`,
		},
		...(itemList ? { mainEntity: itemList } : {}),
	});

	return (
		<Helmet
			meta={!cover && firstProductImage ? [
				{ property: 'og:image', content: firstProductImage },
				{ name: 'twitter:image', content: firstProductImage },
			] : []}
		>
			<script type="application/ld+json">{collectionPageLd}</script>
		</Helmet>
	);
}

function mapStateToProps(store) {
	return {
		products: memoizedGetProducts(store, { isActive: true, isPack: false, isVariation: false }),
	};
}

CategorySeo.propTypes = {
	intl: PropTypes.object.isRequired,
	page: PropTypes.object,
	title: PropTypes.string,
	description: PropTypes.string,
	cover: PropTypes.string,
	products: PropTypes.arrayOf(PropTypes.object),
};

export default connect(mapStateToProps)(injectIntl(CategorySeo));
